import React from 'react';
import { X } from 'lucide-react';
import { colors, typography, radius, spacing } from '../../../theme';

/**
 * "View all N exercises" drilldown for Strength Progress. Reuses the row-list
 * presentation from EnhancedStats.jsx's dead `exercises` tab branch (rank
 * badge, name, two-stat row), re-sourced onto 1RM data instead of that
 * branch's original volume ranking.
 */
const AllExercisesDrilldown = ({ exercises, onClose }) => {
  if (!exercises) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={{ ...typography.sectionHeading, color: colors.text }}>All Exercises</div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={18} color={colors.textMuted} />
          </button>
        </div>
        <div style={styles.list}>
          {exercises.map((ex, index) => (
            <div key={ex.exerciseName} style={styles.row}>
              <div style={styles.rank}>#{index + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: spacing.sm }}>
                  <span style={{ ...typography.body, color: colors.text, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ex.exerciseName}
                  </span>
                  <span style={{ ...typography.body, color: colors.gold, fontWeight: '700', flexShrink: 0 }}>
                    {ex.estimated1RM} kg
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, marginTop: '4px' }}>
                  {ex.movementCategory && (
                    <span style={{ ...typography.caption, color: colors.textSubtle }}>{ex.movementCategory}</span>
                  )}
                  {ex.weight != null && ex.reps != null && (
                    <span style={{ ...typography.caption, color: colors.textMuted }}>
                      best set {ex.weight}kg × {ex.reps}
                    </span>
                  )}
                  {ex.minWeight != null && ex.maxWeight != null && (
                    <span style={{ ...typography.caption, color: colors.textMuted }}>
                      {ex.minWeight}–{ex.maxWeight}kg range
                    </span>
                  )}
                  {ex.lastSessionDate && (
                    <span style={{ ...typography.caption, color: colors.textSubtle }}>
                      last {new Date(ex.lastSessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 1000,
  },
  sheet: {
    width: '100%', maxWidth: '480px', maxHeight: '85vh',
    background: colors.surface1,
    borderTopLeftRadius: radius.modal, borderTopRightRadius: radius.modal,
    padding: spacing.lg,
    overflowY: 'auto',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: spacing.lg,
  },
  closeBtn: {
    background: colors.surface2, border: 'none', borderRadius: '50%',
    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
  },
  list: { display: 'flex', flexDirection: 'column', gap: spacing.sm },
  row: {
    display: 'flex', alignItems: 'center', gap: spacing.md,
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.surface2, borderRadius: radius.sm,
    border: `1px solid ${colors.border}`,
  },
  rank: {
    ...{ fontSize: '11px', fontWeight: '700' },
    color: colors.textMuted, width: '28px', textAlign: 'center', flexShrink: 0,
  },
};

export default AllExercisesDrilldown;
